import { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image } from "lucide-react";
import SEO from "@/components/SEO";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const empty = {
    name: "",
    designation: "",
    department: "",
    quote: "",
    image: "",
    social_url: "",
};

export default function AdminWinners() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);

    // LOAD
    const load = async () => {
        try {
            const res = await api.get("/winners");
            setItems(res.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const setF = (k, v) => {
        setForm((p) => ({ ...p, [k]: v }));
    };

    const openNew = () => {
        setForm(empty);
        setEditId(null);
        setOpen(true);
    };

    const openEdit = (w) => {
        setForm({
            name: w.name || "",
            designation: w.designation || "",
            department: w.department || "",
            quote: w.quote || "",
            image: w.image || "",
            social_url: w.social_url || "",
        });
        setEditId(w.id);
        setOpen(true);
    };

    // ✅ IMAGE UPLOAD (ONLY upload API)
    const uploadImage = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("kind", "image");

        try {
            setUploading(true);

            const res = await api.post("/admin/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const url = res.data.url;
            setF("image", url); // save URL only

            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    // ✅ SAVE (NO UploadFile, ONLY JSON/FormData text fields)
    const save = async (e) => {
        e.preventDefault();
        setBusy(true);

        try {
            const formData = new FormData();

            formData.append("name", form.name);
            formData.append("designation", form.designation);
            formData.append("department", form.department || "");
            formData.append("quote", form.quote || "");
            formData.append("social_url", form.social_url || "");
            formData.append("image", form.image || ""); // URL string

            if (editId) {
                await api.put(`/admin/winners/${editId}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                toast.success("Updated");
            } else {
                await api.post(`/admin/winners`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                toast.success("Created");
            }

            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // DELETE
    const del = async (id) => {
        if (!confirm("Delete this winner?")) return;

        try {
            await api.delete(`/admin/winners/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const fullUrl = (u) =>
        u?.startsWith("/") ? `${BACKEND_URL}${u}` : u;

    return (
        <div>
            <SEO title="Admin - Winners" />
            {/* HEADER */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Winners</h1>

                <button
                    onClick={openNew}
                    className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Winner
                </button>
            </div>

            {/* LIST */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((w) => (
                    <div key={w.id} className="bg-white border rounded-2xl p-5">
                        <div className="flex items-center gap-3">
                            {w.image ? (
                                <img
                                    src={fullUrl(w.image)}
                                    className="w-12 h-12 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                    <Image className="w-4 h-4 text-gray-400" />
                                </div>
                            )}

                            <div>
                                <div className="font-bold">{w.name}</div>
                                <div className="text-xs text-gray-500">
                                    {w.designation}
                                </div>
                            </div>
                        </div>

                        <p className="text-xs mt-3 text-gray-600">
                            "{w.quote}"
                        </p>

                        <div className="flex justify-end gap-2 mt-3">
                            <button
                                onClick={() => openEdit(w)}
                                className="p-1.5 hover:bg-gray-100 rounded"
                            >
                                <Edit className="w-4 h-4" />
                            </button>

                            <button
                                onClick={() => del(w.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg w-[95vw] sm:w-full z-[100]">
                    <DialogHeader>
                        <DialogTitle>
                            {editId ? "Edit Winner" : "Add Winner"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={save} className="space-y-4">

                        {/* IMAGE UPLOAD */}
                        <div>
                            <label className="text-xs font-medium">
                                Upload Image
                            </label>

                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mt-1">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        uploadImage(e.target.files[0])
                                    }
                                    className="text-xs sm:text-sm w-full sm:w-auto"
                                />

                                {uploading && (
                                    <span className="text-xs text-gray-500">
                                        Uploading...
                                    </span>
                                )}
                            </div>

                            {form.image && (
                                <img
                                    src={fullUrl(form.image)}
                                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover mt-2"
                                />
                            )}
                        </div>

                        {/* INPUTS */}
                        <input
                            placeholder="Name"
                            value={form.name}
                            onChange={(e) => setF("name", e.target.value)}
                            className="w-full border px-3 py-2 rounded-lg text-sm"
                        />

                        <input
                            placeholder="Designation"
                            value={form.designation}
                            onChange={(e) => setF("designation", e.target.value)}
                            className="w-full border px-3 py-2 rounded-lg text-sm"
                        />

                        <input
                            placeholder="Department"
                            value={form.department}
                            onChange={(e) => setF("department", e.target.value)}
                            className="w-full border px-3 py-2 rounded-lg text-sm"
                        />

                        <input
                            placeholder="Social URL"
                            value={form.social_url}
                            onChange={(e) => setF("social_url", e.target.value)}
                            className="w-full border px-3 py-2 rounded-lg text-sm"
                        />

                        <textarea
                            placeholder="Quote"
                            value={form.quote}
                            onChange={(e) => setF("quote", e.target.value)}
                            className="w-full border px-3 py-2 rounded-lg text-sm"
                            rows={4}
                        />

                        {/* BUTTONS */}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="px-4 py-2 border rounded-lg text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                disabled={busy}
                                className="bg-black text-white px-4 py-2 rounded-lg text-sm"
                            >
                                {busy ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}